import React, { useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import { useUserId } from '../../../../app/hooks/accounts';
import { useSaveView, useUpdateView, useDeleteView } from '../../../../app/hooks/view';
import { Tab, useTabManager } from '../../../context/tab-manager-context';
import { constructPersistedView } from '../../../utils/view-state-helpers';
import { ConfirmCloseModal } from '../ConfirmCloseModal';
import { ConfirmDeleteModal } from '../ConfirmDeleteModal';
import { SaveViewModal } from '../SaveViewModal';
import { ShareViewLinkModal } from '../ShareViewLinkModal';

import { Dropdown, Modal, ModalContents, useToast } from '@controlrooms/components';
import { ViewState, ViewShareType } from '@controlrooms/models';

interface TabItemMenuProps {
  open: boolean;
  position: { top: number; left: number } | null;
  dropdownRef: React.RefObject<HTMLDivElement>;
  tab: Tab;
  isDirty: boolean;
  currentUserId?: number;
  onClose: () => void;
}

const MenuContainer = styled.div`
  position: absolute;
  z-index: 9999;
`;

export const TabItemMenu: React.FC<TabItemMenuProps> = ({
  open,
  position,
  dropdownRef,
  tab,
  isDirty,
  currentUserId,
  onClose,
}) => {
  const userId = useUserId();
  const {
    tabs = [],
    duplicateTab,
    closeTabWithCollectionCleanup,
    closeOtherTabs,
    deleteTab,
    revertTab,
    getViewState,
    replaceTabIdWithViewId,
    updateActiveTabViewState,
    viewCollection,
    setViewCollection,
  } = useTabManager();
  const { mutateAsync: createView } = useSaveView();
  const { mutateAsync: updatePersistedView } = useUpdateView();
  const { mutateAsync: deleteView } = useDeleteView();
  const toast = useToast();

  // Modal states
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showCloseOtherModal, setShowCloseOtherModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [viewStateForModal, setViewStateForModal] = useState<ViewState | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const effectiveUserId = currentUserId || userId;
  const isSaved = tab.saved;
  const canDelete = isSaved && tab.userId === effectiveUserId;
  const canSave = isSaved && isDirty;
  const canRevert = isSaved && isDirty;
  /** VIEWS tab type (Monitor/Analyze/Investigate); not ALERT_BOARD, LIMIT_MANAGER, or MANAGE */
  const isView = tab.type === 'VIEWS';

  // Save as New Tab (New view) - Only for unsaved tabs - Opens SaveViewModal
  const handleSaveAsNew = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const viewState = await getViewState(tab.id);
      setViewStateForModal(viewState);
      setShowSaveModal(true);
      onClose(); // Close the dropdown
    } catch (error) {
      console.error('Failed to get view state for save:', error);
    }
  };

  // Save Changes (Saved view) - PUT updateView to backend, then sync local state
  const handleSaveChanges = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!tab.viewId) return;
    try {
      setApiError(null);
      const viewState = await getViewState(tab.id);
      const persistView = constructPersistedView({
        viewState,
        viewName: tab.name,
        viewDescription: tab.description ?? '',
        currentUserId: effectiveUserId ?? 0,
        shareType: (viewState.shareType ?? ViewShareType.PRIVATE) as ViewShareType,
        shared: viewState.shareType !== ViewShareType.PRIVATE,
      });
      await updatePersistedView({
        view_id: tab.viewId as string,
        view: persistView,
      });
      const updatedViewState: ViewState = {
        ...viewState,
        name: tab.name,
        description: tab.description ?? '',
        isDirty: false,
      };
      // Pass viewId so context applies update to this tab (active or not) and persists view state + tab meta
      await updateActiveTabViewState({ ...updatedViewState, viewId: tab.id });
      if (viewCollection.collectionId) {
        setViewCollection((prev) => ({
          ...prev,
          views: [...(prev.views || []).filter((v) => v.view_id !== tab.viewId), persistView],
        }));
      }
      onClose();
    } catch (error) {
      console.error('Failed to save changes:', error);
      setApiError('Unable to update');
    }
  };

  // Revert (Saved view) - Revert local changes to original saved state
  const handleRevert = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await revertTab(tab.id);
      onClose();
    } catch (error) {
      console.error('Failed to revert tab:', error);
      setApiError('Unable to revert');
    }
  };

  // Manage (Saved view) - Open Save View Modal
  const handleManage = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const viewState = await getViewState(tab.id);
      setViewStateForModal(viewState);
      setShowSaveModal(true);
      onClose();
    } catch (error) {
      console.error('Failed to get view state for manage:', error);
    }
  };

  // Duplicate (new and saved view)
  const handleDuplicate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await duplicateTab(tab.id);
      onClose();
    } catch (error) {
      console.error('Failed to duplicate tab:', error);
    }
  };

  // Delete (current user's Saved view) - Show confirmation modal
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteModal(true);
    onClose();
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async (confirmed: boolean) => {
    setShowDeleteModal(false);
    if (!confirmed) return;

    try {
      if (!tab.viewId) {
        throw new Error('Tab is not a saved view');
      }

      // Call API to delete the view
      await deleteView(tab.viewId);

      // Update viewCollection if it exists
      if (viewCollection.collectionId) {
        setViewCollection((prev) => ({
          ...prev,
          views: (prev.views || []).filter((view) => view.view_id !== tab.viewId),
          viewsIds: prev.viewsIds.filter((id) => id !== tab.viewId),
          isDirty: true,
        }));
      }

      // Close the tab after successful deletion
      await deleteTab(tab.viewId);
    } catch (error) {
      console.error('Failed to delete tab:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unable to delete';
      toast.error(errorMessage, {
        title: 'Delete Failed',
        autoclose: true,
        autocloseTime: 5000,
      });
    }
  };

  // Share - Open ShareViewLinkModal
  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const viewState = await getViewState(tab.id);
      setViewStateForModal(viewState);
      setShowShareModal(true);
      onClose(); // Close the dropdown
    } catch (error) {
      console.error('Failed to get view state for sharing:', error);
    }
  };

  // Close - Show confirmation and close
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCloseModal(true);
    onClose();
  };

  const handleCloseConfirm = async (confirmed: boolean) => {
    setShowCloseModal(false);
    if (confirmed) {
      closeTabWithCollectionCleanup(tab);
    }
  };

  // Close other tabs - keep only this tab. Show confirmation only when any other tab is dirty.
  const performCloseOtherTabs = useCallback(async () => {
    if (viewCollection.collectionId && tab.viewId) {
      setViewCollection((prev) => ({
        ...prev,
        views: (prev.views || []).filter((v) => v.view_id === tab.viewId),
        viewsIds: prev.viewsIds.filter((id) => id === tab.viewId),
        isDirty: true,
      }));
    }
    await closeOtherTabs(tab.id);
  }, [viewCollection.collectionId, tab.viewId, setViewCollection, closeOtherTabs, tab.id]);

  const handleCloseOtherTabs = (e: React.MouseEvent) => {
    e.stopPropagation();
    const otherTabs = (tabs ?? []).filter((t) => t.id !== tab.id);
    const anyOtherDirty = otherTabs.some((t) => t.isDirty);
    if (anyOtherDirty) {
      setShowCloseOtherModal(true);
      onClose();
    } else {
      performCloseOtherTabs();
      onClose();
    }
  };

  const handleCloseOtherConfirm = async (confirmed: boolean) => {
    setShowCloseOtherModal(false);
    if (confirmed) {
      await performCloseOtherTabs();
    }
  };

  // Handle Save View Modal save
  const handleSaveModalSave = async ({
    name,
    description,
    shareType,
  }: {
    name: string;
    description: string;
    shareType: ViewShareType;
  }) => {
    if (!viewStateForModal) return;

    try {
      setApiError(null);

      if (isSaved && tab.viewId) {
        // Update existing view
        const persistView = constructPersistedView({
          viewState: viewStateForModal,
          viewName: name,
          viewDescription: description,
          currentUserId: effectiveUserId,
          shareType: shareType as ViewShareType,
          shared: shareType !== ViewShareType.PRIVATE,
        });

        // Update saved view via API
        await updatePersistedView({
          view: persistView,
          view_id: tab.viewId,
        });

        // Update ViewState
        const updatedViewState: ViewState = {
          ...viewStateForModal,
          name: name,
          description: description,
          shareType: shareType as ViewShareType,
          shared: shareType !== ViewShareType.PRIVATE,
          isDirty: false,
        };

        // updateActiveTabViewState already updates tab name/description from viewState
        await updateActiveTabViewState(updatedViewState);

        // Update viewCollection if it exists
        if (viewCollection.collectionId) {
          setViewCollection((prev) => ({
            ...prev,
            views: [...(prev.views || []).filter((v) => v.view_id !== tab.viewId), persistView],
            isDirty: true,
          }));
        }
      } else {
        // Create new view from unsaved tab
        // Convert ViewState to PersistView format
        const persistView = constructPersistedView({
          viewState: viewStateForModal,
          viewName: name,
          viewDescription: description,
          currentUserId: effectiveUserId,
          shareType: shareType as ViewShareType,
          shared: shareType !== ViewShareType.PRIVATE,
        });

        // Create new saved view via API
        const viewId = await createView(persistView);

        // Update ViewState with the new view_id
        const updatedViewState: ViewState = {
          ...viewStateForModal,
          view_id: viewId,
          viewId: viewId,
          name: name,
          description: description,
          shareType: shareType as ViewShareType,
          shared: shareType !== ViewShareType.PRIVATE,
          isDirty: false, // No longer dirty since it's saved
        };

        // Replace tab id with viewId: remove tab-<uuid>, persist under tab-<viewId>, set tab.id = viewId
        await replaceTabIdWithViewId(
          tab.id,
          viewId,
          {
            saved: true,
            viewId: viewId,
            userId: effectiveUserId,
            name: name,
            description: description,
            isDirty: false,
          },
          updatedViewState,
        );

        // Update viewCollection if it exists
        if (viewCollection.collectionId) {
          setViewCollection((prev) => ({
            ...prev,
            viewsIds: Array.from(new Set([...prev.viewsIds, viewId])),
            views: [...(prev.views || []).filter((v) => v.view_id !== viewId), persistView],
            isDirty: true,
          }));
        }
      }

      setShowSaveModal(false);
      setViewStateForModal(null);
      onClose();
    } catch (error) {
      console.error('Failed to save tab:', error);
      const err = error as {
        response?: { data?: { detail?: string | string[] } };
        message?: string;
      };
      const backendDetail = err.response?.data?.detail;
      const errorMessage =
        typeof backendDetail === 'string'
          ? backendDetail
          : Array.isArray(backendDetail) && backendDetail.length > 0
          ? backendDetail[0]
          : error instanceof Error
          ? error.message
          : isSaved
          ? 'Unable to update'
          : 'Unable to save';
      toast.error(errorMessage, {
        title: isSaved ? 'Update Failed' : 'Save Failed',
        autoclose: true,
        autocloseTime: 5000,
      });
    }
  };

  // Don't render if no position and no modals are open
  if (
    !position &&
    !showCloseModal &&
    !showCloseOtherModal &&
    !showSaveModal &&
    !showShareModal &&
    !showDeleteModal
  ) {
    return null;
  }

  // Only render dropdown if open and position exists
  const shouldRenderDropdown = open && position;

  const menuContent = (
    <MenuContainer
      ref={dropdownRef}
      style={{
        top: position?.top || 0,
        left: position?.left || 0,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {shouldRenderDropdown && (
        <Dropdown.Options position="center" vertical="bottom" open={open} width="200px">
          {isView && (
            <>
              {/* Save as New Tab - Only for unsaved tabs */}
              {!isSaved && <Dropdown.Item onClick={handleSaveAsNew}>Save as New Tab</Dropdown.Item>}

              {/* Save Changes - Only for saved views with changes */}
              {canSave && <Dropdown.Item onClick={handleSaveChanges}>Save Changes</Dropdown.Item>}

              {/* Revert - Only for saved views with changes */}
              {canRevert && <Dropdown.Item onClick={handleRevert}>Revert</Dropdown.Item>}

              {/* Manage - Only for saved views */}
              {isSaved && <Dropdown.Item onClick={handleManage}>Manage</Dropdown.Item>}

              <Dropdown.Separator />

              {/* Duplicate - Always available */}
              <Dropdown.Item onClick={handleDuplicate}>Duplicate</Dropdown.Item>

              {/* Delete - Only for saved views created by current user */}
              {canDelete && <Dropdown.Item onClick={handleDelete}>Delete</Dropdown.Item>}

              <Dropdown.Separator />

              {/* Share - Always available */}
              <Dropdown.Item onClick={handleShare}>Share</Dropdown.Item>
            </>
          )}
          {!isView && (
            <>
              {/* Duplicate - Always available */}
              <Dropdown.Item onClick={handleDuplicate}>Duplicate</Dropdown.Item>
            </>
          )}

          {/* Close - Always available */}
          <Dropdown.Item onClick={handleClose}>Close</Dropdown.Item>

          {/* Close other tabs - Only when more than one tab */}
          {(tabs?.length ?? 0) > 1 && (
            <Dropdown.Item onClick={handleCloseOtherTabs}>Close other tabs</Dropdown.Item>
          )}
        </Dropdown.Options>
      )}

      {showSaveModal && viewStateForModal && (
        <SaveViewModal
          onClose={() => {
            setShowSaveModal(false);
            setViewStateForModal(null);
            onClose();
          }}
          viewState={viewStateForModal}
          onSave={handleSaveModalSave}
          apiError={apiError}
          isUpdateModal={isSaved}
        />
      )}

      {showShareModal && viewStateForModal && (
        <ShareViewLinkModal
          viewSnapshot={viewStateForModal}
          onClose={() => {
            setShowShareModal(false);
            setViewStateForModal(null);
            onClose();
          }}
        />
      )}

      {showCloseModal && <ConfirmCloseModal onConfirm={handleCloseConfirm} />}

      {showCloseOtherModal && (
        <Modal open={true}>
          <ModalContents
            title="Close other tabs"
            styles={{ content: { minWidth: '450px' } }}
            confirmButtonText="Close others"
            closeButtonText="Cancel"
            closeButtonCallback={() => handleCloseOtherConfirm(false)}
            shouldCloseOnEsc={false}
            confirmButtonCallback={() => handleCloseOtherConfirm(true)}
          >
            <div style={{ padding: '10px', fontSize: '13px' }}>
              <p>Close all other tabs? Unsaved changes in other tabs will be lost.</p>
            </div>
          </ModalContents>
        </Modal>
      )}

      {showDeleteModal && <ConfirmDeleteModal onConfirm={handleDeleteConfirm} />}
    </MenuContainer>
  );

  return ReactDOM.createPortal(menuContent, document.body);
};
